/**
 * Liability and comp budgeted for a sub who has no work order yet.
 *
 * Jonathon, 2026-09-15: "gl and wc also need to be options to select on other
 * expenses based on whether or not the sub has them. that way they are
 * products that are automatically calculated based on the sub in case we're
 * working on estimates that don't generate them."
 *
 * Both policies are rated on what the crew is paid, and until this existed the
 * only thing on an estimate that said what a crew is paid was a work order —
 * so an estimate priced before anybody raised one carried no insurance at all,
 * and the margin at the top of the screen was wrong by the whole of it. This
 * is the way to say "Pablo is doing $12,000 of this" before the order exists.
 *
 * It first shipped as a button of its own under the table, writing a row that
 * sat below the expenses and produced its figures somewhere else. Jonathon,
 * 2026-09-22, having tried it on a job whose crew already had a work order and
 * found a box he could type in that changed nothing: "i'd rather it just be
 * natural like the work order and material order where you just add an item
 * and make the selection there." So the choice of what kind of line to add is
 * on `+ Add Line Item` now, and the sub, the labour and the trade are chosen
 * on the row itself, in the table, beside the amount they produce (ADR 0198).
 *
 * Nobody types an amount. The liability and the comp are worked out by
 * `computeAutoExpenses` against that crew's own certificates, at the rates on
 * policy BAS-83535-1 (rule 3: no arithmetic on money in a component, rule 5:
 * the rates are the company's rule and live in `packages/core`).
 */

import {
  generalLiabilityStanding,
  toCents,
  toDecimal,
  todayCentral,
  workersCompStanding,
  type Crew,
  crewLabel,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import type { EstimateInsuranceLine } from "../../lib/queries";

/**
 * What a crew's papers mean for the money, in a few words.
 *
 * `generalLiabilityStanding` and `workersCompStanding` rather than a read of
 * the columns here: "a certificate we have not seen is budgeted as carrying
 * nothing" is a rule Jonathon set on 2026-09-22, it decides the amount, and a
 * second copy of it in a component is a copy that will one day disagree with
 * the charge.
 */
export function coverageNote(crew: Crew | undefined): string {
  if (!crew) return "";
  const today = todayCentral();
  const gl = generalLiabilityStanding(crew, today);
  const wc = workersCompStanding(crew, today);
  if (crew.is_in_house) return t("our own people — both policies are ours");
  const glSays =
    gl === "insured"
      ? t("carries their own liability")
      : gl === "uninsured"
        ? t("no liability of their own")
        : t("no liability certificate on file — budgeted as if they have none");
  const wcSays =
    wc === "insured"
      ? t("carries their own comp")
      : wc === "uninsured"
        ? t("we carry their comp")
        : t("no comp certificate on file — budgeted as if they have none");
  return `${glSays}; ${wcSays}`;
}

/**
 * The kinds of line `+ Add Line Item` can add.
 *
 * Liability and comp are two entries rather than one because those are the two
 * words anybody looks for, and one row for the sub because the two policies
 * are rated on the same labour: two boxes for one number is two numbers that
 * disagree. Picking either adds the sub's insurance, and what that sub costs —
 * liability alone, or liability and comp — is the certificate's answer, not a
 * choice on this menu.
 */
export interface AddExpenseLineProps {
  /** Active crews, minus any that already have a line on this estimate. */
  availableCrews: readonly Crew[];
  onAddExpense: () => void;
  onAddInsurance: (crewId: string) => void;
}

/**
 * `+ Add Line Item`, and what kind of line it adds.
 *
 * One button, and the choice made in its place — the pattern the work order
 * and material order editors already use, which is what Jonathon asked for on
 * 2026-09-22. Liability and comp are two entries because those are the two
 * words anybody looks for; both add one line for the sub, because the two
 * policies are rated on the same labour and two boxes for one number is two
 * numbers that disagree. What that sub actually costs — liability alone, or
 * liability and comp — is their certificate's answer, not a choice here.
 */
export function AddExpenseLine({
  availableCrews,
  onAddExpense,
  onAddInsurance,
}: AddExpenseLineProps) {
  const [choosing, setChoosing] = useState<"kind" | "sub" | null>(null);

  if (choosing === "sub") {
    return (
      <div className="addins">
        <select
          aria-label={t("Sub to budget insurance for")}
          defaultValue=""
          autoFocus
          onChange={(e) => {
            const id = e.target.value;
            if (!id) return;
            setChoosing(null);
            onAddInsurance(id);
          }}
        >
          <option value="">{t("Choose a sub…")}</option>
          {availableCrews.map((c) => (
            <option key={c.id} value={c.id}>
              {crewLabel(c)}
            </option>
          ))}
        </select>
        <button className="btn-small" onClick={() => setChoosing(null)}>
          {t("Cancel")}
        </button>
      </div>
    );
  }

  if (choosing === "kind") {
    return (
      <div className="addins">
        <button
          className="btn-small"
          onClick={() => {
            setChoosing(null);
            onAddExpense();
          }}
        >
          {t("An expense")}
        </button>
        {/* Two buttons, one line. See the note above. */}
        <button
          className="btn-small"
          disabled={availableCrews.length === 0}
          title={
            availableCrews.length === 0
              ? t("Every sub already has a line on this estimate")
              : t(
                  "Worked out from what the sub is paid, at the rates on the policy",
                )
          }
          onClick={() => setChoosing("sub")}
        >
          {t("General Liability for a sub")}
        </button>
        <button
          className="btn-small"
          disabled={availableCrews.length === 0}
          title={
            availableCrews.length === 0
              ? t("Every sub already has a line on this estimate")
              : t(
                  "Worked out from what the sub is paid, at the rates on the policy",
                )
          }
          onClick={() => setChoosing("sub")}
        >
          {t("Workers Comp for a sub")}
        </button>
        <button className="btn-small" onClick={() => setChoosing(null)}>
          {t("Cancel")}
        </button>
      </div>
    );
  }

  return (
    <div className="addins">
      <button className="addrow" onClick={() => setChoosing("kind")}>
        {t("+ Add Line Item")}
      </button>
    </div>
  );
}

export interface InsuranceRowControlsProps {
  line: EstimateInsuranceLine;
  crews: readonly Crew[];
  /** Shown as the blank trade option — a line with no trade follows it. */
  estimateTrade: string | null;
  tradeNames: readonly string[];
  /** This crew has a work order here, so the line charges nothing. */
  covered: boolean;
  onSave: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

/**
 * Crews a line can be moved to without colliding with another line.
 *
 * The database allows one insurance line per crew per estimate. The row's
 * picker therefore keeps its current crew and excludes every crew already
 * used by a different row, just like the add picker does.
 */
export function insuranceCrewChoices(
  crews: readonly Crew[],
  lines: readonly EstimateInsuranceLine[],
  current: EstimateInsuranceLine,
): readonly Crew[] {
  const usedElsewhere = new Set(
    lines.filter((line) => line.id !== current.id).map((line) => line.crew_id),
  );
  return crews.filter(
    (crew) => crew.id === current.crew_id || !usedElsewhere.has(crew.id),
  );
}

/**
 * The sub, the labour and the trade, on the row in the expenses table.
 *
 * Rendered once per line, on its General Liability row — the comp row beside
 * it is the same labour rated by the second policy, and a second box for it
 * would be a second answer to one question.
 */
export function InsuranceRowControls({
  line,
  crews,
  estimateTrade,
  tradeNames,
  covered,
  onSave,
  onDelete,
}: InsuranceRowControlsProps) {
  const crew = crews.find((c) => c.id === line.crew_id);
  const tradeOptions = [
    ...new Set([...tradeNames, ...(line.trade ? [line.trade] : [])]),
  ];

  return (
    <div className="insrow">
      <select
        aria-label={t("Sub this insurance is for")}
        value={line.crew_id}
        onChange={(e) => void onSave(line.id, { crew_id: e.target.value })}
      >
        {crews.map((c) => (
          <option key={c.id} value={c.id}>
            {crewLabel(c)}
          </option>
        ))}
      </select>
      <input
        className="num"
        inputMode="decimal"
        aria-label={t("Labour for {sub}", {
          sub: crewLabel(crew) ?? crewLabel(line.crew) ?? t("this sub"),
        })}
        placeholder="0"
        disabled={covered}
        defaultValue={
          line.labor_amount == null ? "" : String(line.labor_amount)
        }
        // Saved when the box is left, the rule every other money box on this
        // screen follows. On each keystroke, typing "12000" would reprice the
        // estimate five times on the way past. `toCents` is the parser every
        // other money box uses: "12,000" and "$12,000" are twelve thousand
        // dollars, not twelve.
        onBlur={(e) => {
          const typed = toCents(e.target.value);
          const next = typed > 0 ? typed : toCents(0);
          if (toCents(line.labor_amount) === next) return;
          void onSave(line.id, { labor_amount: toDecimal(next) });
        }}
      />
      <span className="na-hint">{t("of labour")}</span>
      <select
        aria-label={t("Trade for {sub}", {
          sub: crewLabel(crew) ?? t("this sub"),
        })}
        value={line.trade ?? ""}
        onChange={(e) =>
          void onSave(line.id, { trade: e.target.value || null })
        }
      >
        {/* Blank means "whatever this estimate is", which is a real answer and
            follows the estimate rather than freezing a copy of today's trade. */}
        <option value="">{estimateTrade ?? "—"}</option>
        {tradeOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <span className="na-hint">{coverageNote(crew)}</span>
      <button
        className="xbtn"
        title={t("Stop budgeting insurance for this sub")}
        aria-label={t("Remove insurance for {sub}", {
          sub: crewLabel(crew) ?? t("this sub"),
        })}
        onClick={() => void onDelete(line.id)}
      >
        ✕
      </button>
    </div>
  );
}
